import { PATH_ROOT } from "@/routes/paths";
import { useIsDemo } from "@/hooks/common/useIsDemo";
import { Widget } from "./Widget";
import { Briefcase } from "lucide-react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { DragHandleProps } from "../../constants";

const STATUS_COLORS = {
  won: "#5FB84A",
  active: "#3D7DD9",
  atRisk: "#D9B82E",
  lost: "#D94A3D",
} as const;

const STATUS_GRADIENTS: Record<keyof typeof STATUS_COLORS, [string, string]> = {
  won: ["#7FD668", "#2E7A24"],
  active: ["#5FA3F5", "#1E4E99"],
  atRisk: ["#F5D84A", "#8A6E10"],
  lost: ["#F56A5A", "#8A2418"],
};

type MonthlyDatum = {
  month: string;
  won: number;
  active: number;
  atRisk: number;
  lost: number;
  value: string;
};

const DEMO_DATA: MonthlyDatum[] = [
  { month: "Jan", won: 3, active: 4, atRisk: 1, lost: 2, value: "$420K" },
  { month: "Feb", won: 5, active: 3, atRisk: 2, lost: 1, value: "$610K" },
  { month: "Mar", won: 4, active: 6, atRisk: 1, lost: 3, value: "$780K" },
  { month: "Apr", won: 7, active: 5, atRisk: 0, lost: 2, value: "$920K" },
  { month: "May", won: 6, active: 7, atRisk: 3, lost: 1, value: "$1.1M" },
  { month: "Jun", won: 8, active: 6, atRisk: 2, lost: 3, value: "$1.3M" },
  { month: "Jul", won: 5, active: 8, atRisk: 2, lost: 2, value: "$980K" },
  { month: "Aug", won: 9, active: 6, atRisk: 1, lost: 2, value: "$1.4M" },
  { month: "Sep", won: 8, active: 6, atRisk: 2, lost: 3, value: "$1.2M" },
];

const LABELS: Record<keyof typeof STATUS_COLORS, string> = {
  won: "Won",
  active: "Active",
  atRisk: "At Risk",
  lost: "Lost",
};

type TooltipPayload = {
  payload?: MonthlyDatum;
  dataKey?: keyof typeof STATUS_COLORS;
  value?: number;
  color?: string;
};

const ChartTooltip = ({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: TooltipPayload[];
  label?: string;
}) => {
  if (!active || !payload?.length) return null;
  const datum = payload[0]?.payload;
  const total = payload.reduce((sum, p) => sum + (p.value ?? 0), 0);

  return (
    <div className="rounded-lg border border-border bg-background/95 backdrop-blur-sm shadow-lg px-3 py-2 text-xs">
      <div className="flex items-center justify-between gap-4 pb-1.5 mb-1.5 border-b border-border/50">
        <span className="font-semibold text-foreground">{label}</span>
        {datum?.value && (
          <span className="text-muted-foreground tabular-nums">{datum.value}</span>
        )}
      </div>
      <div className="flex flex-col gap-1">
        {payload.map((p) => (
          <div key={p.dataKey} className="flex items-center gap-2">
            <span
              className="size-2 rounded-full shrink-0"
              style={{
                backgroundImage: p.dataKey
                  ? `linear-gradient(180deg, ${STATUS_GRADIENTS[p.dataKey][0]}, ${STATUS_GRADIENTS[p.dataKey][1]})`
                  : undefined,
              }}
            />
            <span className="text-muted-foreground flex-1">
              {p.dataKey ? LABELS[p.dataKey] : ""}
            </span>
            <span className="font-semibold text-foreground tabular-nums">
              {p.value}
            </span>
          </div>
        ))}
        <div className="flex items-center gap-2 pt-1 mt-0.5 border-t border-border/50">
          <span className="text-muted-foreground flex-1">Total deals</span>
          <span className="font-semibold text-foreground tabular-nums">{total}</span>
        </div>
      </div>
    </div>
  );
};

export const PortfolioWidget = ({ dragHandleProps }: { dragHandleProps?: DragHandleProps }) => {
  const isDemo = useIsDemo();

  return (
    <Widget
      title="Deal Pipeline"
      icon={<Briefcase className="size-5" aria-hidden="true" />}
      linkTo={isDemo ? PATH_ROOT.demo.dealPipeline : PATH_ROOT.main.dealPipeline}
      linkText="Manage"
      dragHandleProps={dragHandleProps}
    >
      {!isDemo ? (
        // Real route: a 9-month Won/Active/AtRisk/Lost time-series cannot be
        // honestly derived from the current deal data, and a zeroed chart reads
        // as broken. Show an honest empty-state until the pipeline backend lands.
        <div className="flex-1 flex items-center justify-center">
          <p className="text-muted-foreground text-sm text-center py-4">
            No deal pipeline data yet
          </p>
        </div>
      ) : (
      <div className="flex flex-col gap-2 h-full min-h-0">
        <div className="flex items-center gap-4 text-xs">
          {(Object.keys(STATUS_COLORS) as Array<keyof typeof STATUS_COLORS>).map((key) => (
            <div key={key} className="flex items-center gap-1.5">
              <span
                className="size-2 rounded-full"
                style={{
                  backgroundImage: `linear-gradient(180deg, ${STATUS_GRADIENTS[key][0]}, ${STATUS_GRADIENTS[key][1]})`,
                }}
              />
              <span className="text-muted-foreground">{LABELS[key]}</span>
            </div>
          ))}
        </div>

        <div className="flex-1 min-h-0">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={DEMO_DATA} margin={{ top: 8, right: 4, left: -20, bottom: 0 }}>
              <defs>
                {(Object.keys(STATUS_GRADIENTS) as Array<keyof typeof STATUS_GRADIENTS>).map(
                  (key) => (
                    <linearGradient
                      key={key}
                      id={`portfolio-gradient-${key}`}
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop offset="0%" stopColor={STATUS_GRADIENTS[key][0]} stopOpacity={1} />
                      <stop offset="100%" stopColor={STATUS_GRADIENTS[key][1]} stopOpacity={1} />
                    </linearGradient>
                  ),
                )}
              </defs>
              <CartesianGrid
                strokeDasharray="3 3"
                vertical={false}
                className="stroke-border/50"
              />
              <XAxis
                dataKey="month"
                tick={{ fontSize: 11 }}
                tickLine={false}
                axisLine={false}
                className="text-muted-foreground"
              />
              <YAxis
                tick={{ fontSize: 11 }}
                tickLine={false}
                axisLine={false}
                className="text-muted-foreground"
                allowDecimals={false}
              />
              <Tooltip
                content={<ChartTooltip />}
                cursor={{ fill: "currentColor", opacity: 0.05 }}
              />
              <Bar
                dataKey="won"
                stackId="deals"
                fill="url(#portfolio-gradient-won)"
                radius={[0, 0, 6, 6]}
              />
              <Bar dataKey="active" stackId="deals" fill="url(#portfolio-gradient-active)" />
              <Bar dataKey="atRisk" stackId="deals" fill="url(#portfolio-gradient-atRisk)" />
              <Bar
                dataKey="lost"
                stackId="deals"
                fill="url(#portfolio-gradient-lost)"
                radius={[6, 6, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      )}
    </Widget>
  );
};
